// libraries
import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import G2ReviewBox from 'src/app/ProfileInfo/G2ReviewBox/G2ReviewBox'
import {
  selectErrorDetailMapFromState,
  selectPatchingUserCurrencyFromState,
  selectPatchingUserEmployeeFromState,
  selectPatchingUserFromState,
  selectPatchingUserLocaleFromState,
  selectPatchingUserProfileFromState,
} from 'src/app/ProfileInfo/redux/profileInfo.selectors'
import { useG2ReviewEnabled } from 'src/component-modal/G2ReviewModal/useG2ReviewEnabled'
// styles
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import CurrencyInfoBox from 'src/components/CurrencyInfoBox/CurrencyInfoBox'
import EditProfileInfoBox from 'src/components/EditProfileInfoBox/EditProfileInfoBox'
import EmployeeInfoBox from 'src/components/EmployeeInfoBox/EmployeeInfoBox'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import LocaleInfoBox from 'src/components/LocaleInfoBox/LocaleInfoBox'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import OrganizationBox from 'src/components/OrganizationBox/OrganizationBox'
import VerticalContentCollection from 'src/components/VerticalContentCollection/VerticalContentCollection'
import { useBackgroundDispatch } from 'src/hooks'
import { useQueryOwnUser } from 'src/queries/users.queries'
import {
  patchUser,
  PatchUserActions,
} from 'src/redux/actions/data/users.actions'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { PatchUserRequest } from 'src/requests/patchUser.request'

const ProfileInfo: React.FC = () => {
  const { data: user } = useQueryOwnUser({})
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const patchingUser = useSelector(selectPatchingUserFromState)
  const patchingUserProfile = useSelector(selectPatchingUserProfileFromState)
  const patchingUserLocale = useSelector(selectPatchingUserLocaleFromState)
  const patchingUserCurrency = useSelector(selectPatchingUserCurrencyFromState)
  const patchingUserEmployee = useSelector(selectPatchingUserEmployeeFromState)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const appName = useSelector(selectAppName)
  const isG2ReviewEnabled = useG2ReviewEnabled()

  const dispatch = useBackgroundDispatch()

  const dispatchPatchUser = (
    body: Partial<PatchUserRequest>,
    action: PatchUserActions,
  ): void => {
    dispatch(patchUser(body, action))
  }

  return user !== undefined ? (
    <>
      <Helmet>
        <title>{`Personal Info | ${appName}`}</title>
        <meta name="description" content={`Personal Info page of ${appName}`} />
      </Helmet>
      <BackgroundPageContent>
        <HorizontalContentCollection>
          <VerticalContentCollection>
            <EditProfileInfoBox
              user={user}
              patchingUser={patchingUser}
              patchingUserProfile={patchingUserProfile}
              patchUser={dispatchPatchUser}
              errorDetailMap={errorDetailMap}
            />
            <LocaleInfoBox
              user={user}
              patchingUser={patchingUser}
              patchingUserLocale={patchingUserLocale}
              patchUser={dispatchPatchUser}
              errorDetailMap={errorDetailMap}
            />
            <CurrencyInfoBox
              user={user}
              patchingUser={patchingUser}
              patchingUserCurrency={patchingUserCurrency}
              patchUser={dispatchPatchUser}
              errorDetailMap={errorDetailMap}
            />
          </VerticalContentCollection>
          <VerticalContentCollection>
            {organization !== undefined && (
              <OrganizationBox user={user} organization={organization} />
            )}
            <EmployeeInfoBox
              user={user}
              patchingUser={patchingUser}
              patchingUserEmployee={patchingUserEmployee}
              patchUser={dispatchPatchUser}
              errorDetailMap={errorDetailMap}
            />
            {isG2ReviewEnabled && <G2ReviewBox />}
          </VerticalContentCollection>
        </HorizontalContentCollection>
      </BackgroundPageContent>
    </>
  ) : (
    <NotFoundBackgroundPageContent />
  )
}

export default ProfileInfo
